/**
 * Front-end slider for the Product Promotion block.
 *
 * Swiper's own styles come from the theme's `qala-theme-swiper` handle, which
 * the block declares as a dependency of its stylesheet.
 */
import Swiper from 'swiper';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules';

const BLOCK = 'wp-block-qala-blocks-product-promo';
const SELECTOR = `.${ BLOCK }__slider`;

// Must match styles__block.scss: the CSS lays the slides out before Swiper runs,
// so any difference shows up as a jump on initialisation.
const PRODUCT_GRID = 1240;
const COLUMNS_BELOW_PRODUCT_GRID = 2;

/**
 * Resolve a CSS length to pixels by measuring it.
 *
 * The gap is authored in CSS in any unit, so measure rather than parse. The probe
 * goes inside the wrapper to inherit its custom properties.
 *
 * @param {HTMLElement} wrapper The slides wrapper.
 * @param {string}      value   A CSS length, which may reference custom properties.
 * @return {number} The resolved width in pixels.
 */
const toPixels = ( wrapper, value ) => {
	const probe = document.createElement( 'div' );

	probe.style.cssText = `position:absolute;visibility:hidden;height:0;width:${ value };`;
	wrapper.appendChild( probe );

	const pixels = probe.getBoundingClientRect().width;

	probe.remove();

	return pixels;
};

/**
 * Read the slider options the block wrote onto the element.
 *
 * @param {HTMLElement} element The slider element.
 * @return {Object} Parsed options, or an empty object when unreadable.
 */
const getOptions = element => {
	try {
		return JSON.parse( element.dataset.qalaProductPromo || '{}' );
	} catch ( error ) {
		return {};
	}
};

/**
 * Initialise a single slider.
 *
 * @param {HTMLElement} element The slider element.
 * @return {void}
 */
const initSlider = element => {
	if ( element.dataset.qalaProductPromoReady ) {
		return;
	}

	element.dataset.qalaProductPromoReady = '1';

	const options = getOptions( element );
	const viewport = element.closest( `.${ BLOCK }__viewport` );
	const wrapper = element.querySelector( '.swiper-wrapper' );

	// Read the column count and gap back from CSS so the two cannot drift apart.
	const columns = parseInt( wrapper.dataset.columns, 10 ) || options.slidesPerView || 4;
	const gap = toPixels( wrapper, 'var(--gap-base)' );
	const gapWide = toPixels( wrapper, 'var(--gap-wide)' );

	const config = {
		modules: [ A11y, Keyboard, Navigation, Pagination ],
		slidesPerView: COLUMNS_BELOW_PRODUCT_GRID,
		spaceBetween: gap,
		loop: !! options.loop,
		keyboard: { enabled: true },
		watchOverflow: true,
		// Re-measure on reveal or resize; a slider laid out while hidden keeps stale widths.
		observer: true,
		observeParents: true,
		resizeObserver: true,
		breakpoints: {
			[ PRODUCT_GRID ]: { slidesPerView: columns, spaceBetween: gapWide },
		},
	};

	if ( options.arrows && viewport ) {
		config.navigation = {
			prevEl: viewport.querySelector( `.${ BLOCK }__arrow--prev` ),
			nextEl: viewport.querySelector( `.${ BLOCK }__arrow--next` ),
		};
	}

	if ( options.pagination ) {
		config.pagination = {
			el: element.querySelector( '.swiper-pagination' ),
			clickable: true,
		};
	}

	new Swiper( element, config );
};

/**
 * Initialise every slider currently in the document.
 *
 * @return {void}
 */
const initAll = () => {
	document.querySelectorAll( SELECTOR ).forEach( initSlider );
};

// On load, not DOMContentLoaded: the card images settle the container size, and
// measuring before that bakes in the wrong slide widths.
if ( document.readyState === 'complete' ) {
	initAll();
} else {
	window.addEventListener( 'load', initAll );
}

// The block can sit above a FacetWP-driven listing, which re-renders on filtering.
document.addEventListener( 'facetwp-loaded', initAll );
